import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext } from 'react';

import { CreatePaymentPlanDto } from 'src/api/paymentPlanService/dto/CreatePaymentPlanDto';
import { PaymentPlanDto } from 'src/api/paymentPlanService/dto/PaymentPlanDto';
import { createPaymentPlan } from 'src/api/paymentPlanService/requests';
import { ErrorMessage } from 'src/shared/enum/ErrorMessage';

import { queryRetry } from '../query-retry';
import { PaymentPlanChoices } from '../../shared/types/user-choices';
import { Room } from '../../shared/types/room';
import { AuthContext } from '../../providers/auth-context';

type UseFetchCreatePaymentPlanOptions = {
    userChoices: PaymentPlanChoices | null;
    promotionId: string | null;
    isRebooker: boolean;
    fillerWeekEligible: boolean;
    roomData: Room | null;
    bookingId: string | null;
    amend?: boolean;
    adminFee?: boolean;
};

export const useCreatePaymentPlan = () =>
    useMutation<PaymentPlanDto, AxiosError, CreatePaymentPlanDto>({
        mutationKey: ['createPaymentPlan'],
        mutationFn: (paymentPlanData) => createPaymentPlan(paymentPlanData),
        retry: queryRetry,
    });

export const useFetchCreatePaymentPlan = ({
    userChoices,
    promotionId,
    isRebooker,
    fillerWeekEligible,
    roomData,
    bookingId,
    amend = false,
    adminFee = false,
}: UseFetchCreatePaymentPlanOptions) => {
    const hasUserChoices = Boolean(
        userChoices &&
            userChoices.numberOfStudents &&
            userChoices.instalmentType &&
            userChoices.term &&
            userChoices.instalmentId
    );

    const hasRoomData = Boolean(roomData);

    const enabled = hasUserChoices && hasRoomData;
    const userState = useContext(AuthContext);
    const userId = userState.session?.user.id ?? null;

    return useQuery({
        enabled,
        queryKey: [
            'createPaymentPlan',
            userChoices,
            promotionId,
            isRebooker,
            fillerWeekEligible,
            roomData,
            amend,
            adminFee,
            bookingId,
            userId,
        ],
        queryFn: () => {
            const room = roomData as Room;

            if (!userChoices) throw new Error(ErrorMessage.NO_USER_CHOICES_PROVIDED);

            const payload = new CreatePaymentPlanDto({
                selectedStudents: userChoices.numberOfStudents,
                instalmentType: userChoices.instalmentType,
                selectedTerm: userChoices.term,
                instalmentId: userChoices.instalmentId,
                academicYear: room.academic_year,
                roomCode: room.room_code,
                basePrice: room.base_price,
                promotionId,
                isRebooker,
                fillerWeekEligible,
                amend,
                adminFee,
                bookingId,
            });

            return createPaymentPlan(payload);
        },
        retry: queryRetry,
        staleTime: Infinity,
    });
};
